import type { RunEvent } from "@/lib/agent-run-progress"
import { AssistantSuggestionsEvent } from "@/lib/assistant-suggestions-events"
import { ForYouEvent } from "@/lib/for-you-events"
import { HotelSiteViewEvent } from "@/lib/hotel-site-view-events"
import { ProactiveAgentEvent } from "@/lib/proactive-agent-events"
import { Notification } from "../types/notification"
import { EvaluationRunStatus } from "./agent"
import { BotMessageSubtype, PersistentMessage } from "./chat"
import { CollectionRef } from "./collection"
import { FlightBadgeContract } from "./flights/flight-badge"
import { FlightBooking } from "./flights/flight-booking"
import { HotelBooking } from "./hotels/hotels"
import { PlaceSummary } from "./place"
import { UserPresenceType } from "./presence"
import {
  Collaborator,
  ItineraryReviewResponse,
  TripAdvicesResponse,
  TripChange,
} from "./trip"
import { UserRef } from "./user"

export const SOCKET_PATH = "/mtpipe/"

// The slow-response delivery scheme this client implements, declared to the
// socket server in the handshake auth payload. Version 1 receives bodies over
// the socket. Version 2 fetches only from GET /api/streamed-responses/{id}
// when "slow-response-ready" fires (or by polling); it never consumes a
// socket-delivered body, including when the server could not stash one.
export const SLOW_RESPONSE_VERSION = 2

export interface ClientToServerEvents {
  subscribe: (
    objectType: SubscribableObjectType,
    objectId: string | number,
    onSubscribeOrFailure: (response: SubscribeResponse) => void
  ) => void

  unsubscribe: (subscriptionId: string) => void

  "announce-presence-change": (
    tripId: number,
    presenceType: UserPresenceType
  ) => void

  // TODO: Not sure why we need to list reserved events that
  // are supposed to be built-in.
  connect_error: (err: Error) => void
}

export interface ServerToClientEvents {
  "app-version": (version: string | undefined) => void
  // Version-1 browser clients receive the body as a raw JSON string forwarded
  // verbatim; legacy iOS clients are also version 1 but need the object decoded.
  // Version-2 clients never receive this event.
  "slow-response": (data: SlowResponseData | string) => void
  // The body-less notification that a streamed response is ready:
  // version-2 clients fetch it from GET /api/streamed-responses/{id}
  // instead of receiving "slow-response".
  "slow-response-ready": (id: string) => void
  notification: (event: NotificationEventData) => void
  object: (event: SubscribableObjectEvent) => void
  analytics: (event: AnalyticsEventData) => void
  calendar_sync: (event: CalendarSyncEventData) => void
  assistant_suggestions: (event: AssistantSuggestionsEvent) => void
  for_you: (event: ForYouEvent) => void
  proactive_agent: (event: ProactiveAgentEvent) => void
  agent_run: (event: RunEvent) => void
  hotel_site_view: (event: HotelSiteViewEvent) => void
  auth_expired: () => void
}

export type CalendarSyncProvider = "google" | "ical"
export type CalendarSyncPhase =
  | "quick_sync"
  | "initial_sync"
  | "disconnect"
  | "entity_resolution"
  | "preview_summary"
  | "preview_scan"
  | "preview_trips"
  | "preview_map"
  | "preview_story"
export type CalendarSyncState = "queued" | "running" | "completed" | "failed"

export type CalendarSyncMetadata = Partial<{
  fetched: number
  raw_events: number
  travel_events: number
  resolved: number
  trip_count: number
  city_count: number
  trip_path_count: number
  countries_visited: number
  classified: number
  travel_changed: number
  source_scan_updated_at: string
}>

export interface CalendarSyncEventData {
  provider: CalendarSyncProvider
  connection_id: number
  sync_session_id: number | null
  phase: CalendarSyncPhase
  state: CalendarSyncState
  error_message: string | null
  occurred_at: string
  metadata?: CalendarSyncMetadata
}

export type SubscribableObjectType = SubscribableObjectEvent["object_type"]
export type SubscribableObjectId = SubscribableObjectEvent["object_id"]
export type SubscribableObjectEventCallback<T extends SubscribableObjectEvent> =
  (payload: T["data"]) => void
export type SubscribableObjectEvent =
  | PlaceEvent
  | FlightBookingEvent
  | HotelBookingEvent
  | TripEvent
  | EmailVerifyEvent
  | CollaboratorEvent
  | FolioDocumentUserEvent
  | AgentSessionEvent
  | VoiceChatEvent
  | EvaluationRunEvent
  | CopyProductionPackageEvent
  | SavedCollectionEvent

export type SubscribeResponse =
  | { status: "authorized"; subscriptionId: string }
  | { status: "not authorized" }
  | { status: "error"; reason: string }

interface AbstractObjectEvent {
  type: "object"
}

export interface PlaceEvent extends AbstractObjectEvent {
  object_type: "place"
  object_id: string
  data: PlaceEventData
}

export interface FlightBookingEvent extends AbstractObjectEvent {
  object_type: "flight_booking"
  object_id: number
  data: FlightBookingEventData
}

export interface HotelBookingEvent extends AbstractObjectEvent {
  object_type: "hotel_booking"
  object_id: string
  data: HotelBookingEventData
}

export type FlightBookingEventData =
  | {
      type: "update"
      flight_booking: FlightBooking // TODO: consider sending just id
      properties: undefined
    }
  | {
      type: "recoverable_errors"
      flight_booking: FlightBooking // TODO: consider sending just id
      properties: {
        errors: string[]
      }
    }

export interface TripEvent extends AbstractObjectEvent {
  object_type: "trip"
  object_id: number
  data: TripEventData
}

export interface CollaboratorEvent extends AbstractObjectEvent {
  object_type: "collaborator"
  object_id: UserRef
  data: CollaboratorEventData
}

export interface EmailVerifyEvent extends AbstractObjectEvent {
  object_type: "email_verify"
  object_id: UserRef
  data: EmailVerifyEventData
}

export interface FolioDocumentUserEvent extends AbstractObjectEvent {
  object_type: "folio_document_user"
  object_id: UserRef
  data: FolioDocumentUserEventData
}

export interface AgentSessionEvent extends AbstractObjectEvent {
  object_type: "agent_session"
  object_id: number
  data: AgentSessionEventData
}

export interface VoiceChatEvent extends AbstractObjectEvent {
  object_type: "voice_chat"
  object_id: number
  data: VoiceChatEventData
}

export interface EvaluationRunEvent extends AbstractObjectEvent {
  object_type: "agent_evaluation_run"
  object_id: number
  data: EvaluationRunEventData
}

export interface CopyProductionPackageEvent extends AbstractObjectEvent {
  object_type: "copy_production_package"
  object_id: number
  data: CopyProductionPackageEventData
}

export interface SavedCollectionEvent extends AbstractObjectEvent {
  object_type: "saved_collection"
  object_id: CollectionRef
  data: SavedCollectionEventData
}

// Carries no payload: the client re-reads the collection.
export type SavedCollectionEventData = {
  type: "collaborators_changed" | "places_changed"
}

export type CopyProductionPackageEventData = {
  type: "asset_updated"
  asset_id: number
  status: string
}

export type VoiceChatEventData =
  | {
      type: "chat_renamed"
      chat_id: number
      name: string
    }
  | {
      type: "tool_call_progress"
      call_id: string
      handle?: string
      status_message?: string
      estimated_completion_percent?: number
      complication_found?: string
      metric_payload?: AgentStatusMetricPayload
    }
  | {
      type: "tool_call_completed"
      call_id: string
      handle: string
    }
  | {
      type: "tool_call_failed"
      call_id: string
      handle: string
    }

export type TripEventData =
  | TripEventChangeData
  | TripEventAdviceUpdatedData
  | TripEventNeedsAttentionItemUpdatedData
  | TripEventItineraryReviewData
  | TripEventMessageChangedData
  | TripEventMessageDeletedData
  | TripEventChatMovedData
  | TripEventChatBecameGroupChat
  | TripEventCollaboratorAddedData
  | TripEventCollaboratorRemovedData
  | TripEventGroupChatMessageData
  | TripEventGroupChatBotResponseBeganData
  | TripEventGroupChatBotResponseFinishedData
  | TripEventPresenceChangedData
  | TripEventFoundBookingsData
export interface TripEventChangeData {
  type: "change"
  change: TripChange
}

export interface TripEventAdviceUpdatedData {
  type: "advice_updated"
  advices_response: TripAdvicesResponse
}

/** Carries no payload: bookings found in the owner's email were routed to the trip, so clients re-read. */
export interface TripEventFoundBookingsData {
  type: "found_bookings"
}

/** Carries no payload: each member's needs-attention list is their own, so clients re-read. */
export interface TripEventNeedsAttentionItemUpdatedData {
  type: "needs_attention_item_updated"
}

export interface TripEventItineraryReviewData {
  type: "itinerary_review"
  review_response: ItineraryReviewResponse
}

export interface TripEventCollaboratorAddedData {
  type: "collaborator_added"
  collaborator: Collaborator
}

export interface TripEventCollaboratorRemovedData {
  type: "collaborator_removed"
  user_ref: UserRef
  removed_by_user_ref: UserRef
}

export interface TripEventChatBecameGroupChat {
  type: "chat_became_group_chat"
  chat_id: number
}

// chat_id is absent from servers that predate trips having several group chats
export interface TripEventGroupChatMessageData {
  type: "group_chat"
  chat_id?: number
  latest_message_id: number
}

export interface TripEventGroupChatBotResponseBeganData {
  type: "group_chat_begin_streaming_bot_response"
  chat_id?: number
  stream_id: string
  responding_to_message_id?: number
  responding_to_user_ref?: UserRef
  sub_type?: BotMessageSubtype
}

export interface TripEventGroupChatBotResponseFinishedData {
  type: "group_chat_end_streaming_bot_response"
  chat_id?: number
  stream_id: string
  // null when the response failed or was stopped before producing a message
  bot_message_id?: number | null
}

export interface TripEventMessageChangedData {
  type: "message_changed"
  chat_id: number
  message: PersistentMessage
}

export interface TripEventMessageDeletedData {
  type: "message_deleted"
  chat_id: number
  message_id: number
}

export interface TripEventChatMovedData {
  type: "chat_moved"
  chat_id: number
}

export interface TripEventPresenceChangedData {
  type: "presence_changed"
  user_ref: UserRef
  presence_type: UserPresenceType
}

export type CollaboratorEventData =
  | CollaboratorActiveEventData
  | CollaboratorUpdatedEventData

export interface CollaboratorActiveEventData {
  type: "active"
}

export interface CollaboratorUpdatedEventData {
  type: "updated"
}

export interface HotelBookingEventData {
  hotel_booking: HotelBooking // TODO: or maybe just hotel booking id
}

export interface PlaceEventData {
  place: PlaceSummary // TODO: or maybe just entity id
}

export interface NotificationEventData {
  notification: Notification
  unread: {
    count: number
    latestId?: number
  }
}

export interface SlowResponseData<T = unknown> {
  id: string
  status: "success" | "error"
  result: T
}

export interface EmailVerifyEventData {
  verified_ref: UserRef
}

export interface FolioDocumentUserEventData {
  user_ref: UserRef
}

// WebSocket event data for agent sessions
export type AgentSessionEventData =
  | AgentSessionUpdatedEventData
  | AgentSessionEventUpdatedEventData
  | AgentStatusUpdateEventData
  | AgentStreamChunkEventData

export interface AgentSessionUpdatedEventData {
  type: "session_updated"
  session_id: number
  sub_agent_session_id?: number
}

export interface AgentSessionEventUpdatedEventData {
  type: "event_updated"
  event_type: string
  event_id: number
  session_id: number
  sub_agent_session_id?: number
}

export interface AgentTimeEstimateRange {
  min_pct: number
  max_pct: number
  p50_remaining: number
  p90_remaining: number
  sample_size: number
}

export interface AgentTimeEstimateLabels {
  thinking_longer: string
  estimate_default: string
  overtime: string
  almost_done: string
  about_15s: string
  about_30s: string
  under_a_minute: string
  about_a_minute: string
  a_minute_or_two: string
  couple_of_minutes: string
  few_minutes: string
}

export interface AgentTimeEstimate {
  difficulty: "simple" | "moderate" | "complex"
  estimated_total_seconds: { p50: number; p75: number; p90: number }
  ranges: AgentTimeEstimateRange[]
  labels?: AgentTimeEstimateLabels
}

export interface AgentStatusMetricPayload {
  status_message?: string
  estimated_completion_percent?: number
  complication_found?: string
  source?: string
  active_skills?: string[]
  metrics?: AgentStatusMetric[]
  visualizations?: AgentStatusVisualization[]
  time_estimate?: AgentTimeEstimate
}

export interface AgentStatusUpdateEventData extends AgentStatusMetricPayload {
  type: "status_update"
  status_message: string
  session_id: number
  sub_agent_session_id?: number
  timestamp: string
}

export interface AgentStreamChunkEventData {
  type: "stream_chunk"
  session_id: number
  sub_agent_session_id?: number
  request_id: string
  event_id?: number
  role: "assistant"
  chunk_type:
    | "text"
    | "tool_call"
    | "reasoning"
    | "web_search_start"
    | "web_search"
    | "done"
    | "error"
  text_delta?: string
  tool_call?: Record<string, unknown>
  metadata?: Record<string, unknown>
  timestamp: string
}
export type AgentStatusSnapshotResponse = AgentStatusUpdateEventData

// WebSocket event data for evaluation runs
export type EvaluationRunEventData = EvaluationRunUpdatedEventData
export interface AgentStatusMetric {
  key: string
  label: string
  value: number | string | AgentStatusPriceSpreadValue | Record<string, unknown>
  value_display?: string
  unit?: string
}

export interface AgentStatusPriceHistogramBucket {
  min_price: number
  max_price: number
  count: number
  bucket_type?: "inlier" | "outlier_low" | "outlier_high" | string
  label?: string
  tooltip?: string
}

export interface AgentStatusPriceSpreadValue {
  min: number
  max: number
  sample_count?: number
  bucket_strategy?: string
  bucket_count?: number
  inlier_min?: number
  inlier_max?: number
  low_outlier_threshold?: number
  high_outlier_threshold?: number
  low_outlier_count?: number
  high_outlier_count?: number
  outlier_count?: number
  histogram_buckets?: AgentStatusPriceHistogramBucket[]
}

export interface AgentStatusVisualizationBucket {
  month: string
  label: string
  min_price: number
  sample_count: number
  available_count?: number
  unavailable_count?: number
  color: "green" | "yellow" | "orange" | "red" | "dark_red"
  is_cheapest?: boolean
  tooltip?: string
}

export interface AgentStatusEntityPreviewItem {
  entity_id: string
  entity_ref: string
  label?: string
  min_price?: number
  max_price?: number
  live_count?: number
  availability_count?: number
  quote_count?: number
  latest_row_id?: number
}

export interface AgentStatusSourcePreviewItem {
  source_url: string
  source_site?: string
  title_snippet?: string
  relevance?: "high" | "medium" | "low" | string
  hit_count?: number
  latest_row_id?: number
}

export interface AgentStatusQuotePreviewItem {
  quote_ref: string
  entity_ref: string
  label?: string
  suggestion_reason?: string
  price?: number
  price_with_taxes_and_fees?: number
  /** points a night for a points (award) quote, which has no cash price */
  points_per_night?: number
  currency?: string
  check_in_date?: string
  check_out_date?: string
  night_count?: number
  latest_row_id?: number
}

export interface AgentStatusFlightSearchFilters {
  include_airlines?: string[]
  exclude_airlines?: string[]
  alliance?: string
  nonstop_only?: boolean
  max_stops?: number
  departure_time_earliest?: string
  departure_time_latest?: string
  arrival_time_earliest?: string
  arrival_time_latest?: string
  max_price?: number
  max_duration_minutes?: number
}

export interface AgentStatusFlightAirline {
  iata?: string
  name: string
  logo_url?: string
}

export interface AgentStatusFlightAirportEndpoint {
  type: "airport"
  code: string
  name: string
  city: string
  country: string
  time_zone: string
}

export interface AgentStatusFlightLocationEndpoint {
  type: "location"
  city: string
  country_code: string
  iatas: string[]
}

export type AgentStatusFlightEndpoint =
  | AgentStatusFlightAirportEndpoint
  | AgentStatusFlightLocationEndpoint

export interface AgentStatusFlightSearchPreviewSlice {
  origin: AgentStatusFlightEndpoint
  destination: AgentStatusFlightEndpoint
  date: string
  travel_class?: string
  result_count?: number
  price_min?: number
  price_max?: number
  stop_distribution?: Record<string, number>
  airlines?: AgentStatusFlightAirline[]
}

export interface AgentStatusFlightSearchPreviewItem {
  search_id: number
  origin: AgentStatusFlightEndpoint
  destination: AgentStatusFlightEndpoint
  departure_date: string
  return_date?: string
  trip_type: string
  travel_class: string
  status?: "pending" | "complete" | "empty"
  result_count: number
  price_min?: number
  price_max?: number
  currency: string
  airlines: AgentStatusFlightAirline[]
  stop_distribution: Record<string, number>
  annotated_count: number
  slices?: AgentStatusFlightSearchPreviewSlice[]
  filters?: AgentStatusFlightSearchFilters
  latest_row_id?: number
}

export interface AgentStatusFlightNoteSlice {
  origin: AgentStatusFlightEndpoint
  destination: AgentStatusFlightEndpoint
  departure_time: string
  stops?: number
  airlines?: AgentStatusFlightAirline[]
  flight_number?: string
}

export interface AgentStatusFlightNotePreviewItem {
  result_id: number
  search_id: number
  trip_type: string
  slices: AgentStatusFlightNoteSlice[]
  airlines: AgentStatusFlightAirline[]
  flight_number?: string
  stops: number
  total_price?: number
  currency: string
  tags: string[]
  badges?: FlightBadgeContract[]
  notes?: string
  latest_row_id?: number
}

export interface AgentStatusVisualizationRanking {
  strategy?: string
  max_items?: number
  [key: string]: unknown
}

export interface AgentStatusVisualizationBase {
  key: string
  type: string
  title?: string
  metric_key?: string
  total_count?: number
  area_count?: number
  unavailable_pct?: number
  ranking?: AgentStatusVisualizationRanking
  items?: Array<
    | AgentStatusEntityPreviewItem
    | AgentStatusSourcePreviewItem
    | AgentStatusQuotePreviewItem
    | AgentStatusFlightSearchPreviewItem
    | AgentStatusFlightNotePreviewItem
  >
  render_mode?: "chips" | "strip"
  buckets?: AgentStatusVisualizationBucket[]
}

export interface AgentStatusHeatmapVisualization extends AgentStatusVisualizationBase {
  type: "monthly_price_heatmap"
  render_mode?: "chips" | "strip"
  buckets: AgentStatusVisualizationBucket[]
}

export interface AgentStatusEntityPreviewVisualization extends AgentStatusVisualizationBase {
  type: "entity_preview_stack"
  items: AgentStatusEntityPreviewItem[]
  unavailable_pct?: number
}

export interface AgentStatusSourcePreviewVisualization extends AgentStatusVisualizationBase {
  type: "source_preview_stack"
  items: AgentStatusSourcePreviewItem[]
}

export interface AgentStatusQuotePreviewVisualization extends AgentStatusVisualizationBase {
  type: "quote_preview_stack"
  items: AgentStatusQuotePreviewItem[]
}

export interface AgentStatusFlightSearchPreviewVisualization extends AgentStatusVisualizationBase {
  type: "flight_search_preview_stack"
  items: AgentStatusFlightSearchPreviewItem[]
}

export interface AgentStatusFlightNotePreviewVisualization extends AgentStatusVisualizationBase {
  type: "flight_note_preview_stack"
  total_count?: number
  max_display?: number
  items: AgentStatusFlightNotePreviewItem[]
}

/** The loyalty agent's running counts while a search works out how to pay (Agent::Status::Providers::LoyaltyProgramsProvider). */
export interface AgentStatusLoyaltyProgressVisualization extends AgentStatusVisualizationBase {
  type: "loyalty_progress"
  hotels_weighed: number
  cash_wins: number
  points_wins: number
  quotes_with_points: number
  icon_domains?: string[]
}

export type AgentStatusVisualization =
  | AgentStatusHeatmapVisualization
  | AgentStatusLoyaltyProgressVisualization
  | AgentStatusEntityPreviewVisualization
  | AgentStatusSourcePreviewVisualization
  | AgentStatusQuotePreviewVisualization
  | AgentStatusFlightSearchPreviewVisualization
  | AgentStatusFlightNotePreviewVisualization
  | AgentStatusVisualizationBase

// WebSocket event data for agent evaluations
export type AgentEvaluationEventData = EvaluationRunUpdatedEventData

export interface EvaluationRunUpdatedEventData {
  type: "run_updated"
  run_id: number
  status: EvaluationRunStatus
  evaluation_id: number
}

export interface AnalyticsEventData {
  name: string
  properties: Record<string, unknown>
}
